import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import * as M from "../movement.mjs";
import { HEX_SIZE, axialToWorld, worldToAxial, tileHeight } from "../hex-layout.mjs";
import * as Models from "./models.js";

const GVS = 0x5ec8f0;
const BOE = 0xf05a48;
const TERRAIN = {
  plains: 0x6a9a44,
  ore: 0xd4a53c,
  ruins: 0x8f7a62,
  mountain: 0x6f6b66,
  plateau: 0x7d848f,
  pit: 0x23262b,
  forest: 0x3f6b2c,
  hills: 0x8f8a58,
  water: 0x3c6463, // seabed; the translucent sea plane sits above it
  ford: 0x7d8f74,
  road: 0xa08d6a,
};
const SAND = 0xcdb98a;
const RELIEF = 0.1;
// Aircraft fly this far above the tile they occupy.
export const ALTITUDE = { helicopter: 1.25, jet: 2.1 };
const TILE_R = HEX_SIZE * 0.985; // near-seamless land; the faint edge grid keeps cells readable // visual lift per relief step on generated maps (rules ignore it)

// Cheap stable per-hex hash in [0, 1) for colour jitter and prop placement.
const hash2 = (q, r) => { const h = Math.sin(q * 127.1 + r * 311.7) * 43758.5453; return h - Math.floor(h); };

// Overlay palette, shared with the legend chips in index.html / style.css.
export const MARK = {
  reach: 0x3ddc84,
  path: 0xf0c040,
  drop: 0x3ee6ff,
  threat: 0xff4a3d,
  attack: 0xffa12e,
  hover: 0xffffff,
  selected: 0xffffff,
};

const coarse = () => window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 720;
const prefersReduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function mixHex(a, b, t) {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

function skyTexture() {
  const c = document.createElement("canvas");
  c.width = 2; c.height = 256;
  const g = c.getContext("2d"), grad = g.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0, "#4a78a8"); grad.addColorStop(0.55, "#9db6cc"); grad.addColorStop(1, "#c9d4dc");
  g.fillStyle = grad; g.fillRect(0, 0, 2, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// Grain + soft blotches multiplied into every tile's colour so ground reads as grass, dirt and rock.
function detailTexture() {
  const n = 128, c = document.createElement("canvas");
  c.width = c.height = n;
  const g = c.getContext("2d"), img = g.createImageData(n, n);
  let seed = 7;
  const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let i = 0; i < n * n; i++) {
    const v = 214 + rnd() * 41;
    img.data[i * 4] = v; img.data[i * 4 + 1] = v; img.data[i * 4 + 2] = v; img.data[i * 4 + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  for (let k = 0; k < 40; k++) {
    g.fillStyle = `rgba(${rnd() < 0.5 ? "60,50,30" : "255,255,235"},${0.05 + rnd() * 0.07})`;
    g.beginPath(); g.arc(rnd() * n, rnd() * n, 4 + rnd() * 14, 0, Math.PI * 2); g.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

function shade(c, k) {
  const f = (v) => Math.max(0, Math.min(255, Math.round(v * k)));
  return (f((c >> 16) & 255) << 16) | (f((c >> 8) & 255) << 8) | f(c & 255);
}

function yawFor(facing) {
  return -(((facing % 6) + 6) % 6) * Math.PI / 3;
}

function shortestYaw(from, to) {
  let d = to - from;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return from + d;
}

export class HexWorld {
  constructor(canvas) {
    this.canvas = canvas;
    this.state = null;
    this.view = { selected: new Set(), hover: null, path: [], threats: [], reach: [], attacks: [], drops: [] };
    this.tileIndex = new Map();
    this.oreIndex = [];
    this.waterIndex = [];
    this.cells = [];
    this.bounds = null;
    this.units = new Map();
    this.bases = new Map();
    this.structures = new Map();
    this.wrecks = new Map();
    this.fx = [];
    this.lockMesh = null;
    this.mobile = coarse();
    this.reduceMotion = prefersReduced();
    this._scratchCol = new THREE.Color();
    this._dummy = new THREE.Object3D();
    this._hide = new THREE.Object3D();
    this._hide.scale.set(0, 0, 0);
    this._hide.updateMatrix();
    this._plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this._ray = new THREE.Raycaster();
    this._ndc = new THREE.Vector2();
    this._hit = new THREE.Vector3();

    this.scene = new THREE.Scene();
    // Daylight sky: gradient backdrop with haze that matches the fog.
    this.scene.background = skyTexture();
    this.scene.fog = new THREE.Fog(0xb9c8d4, 32, 58);

    this.camera = new THREE.PerspectiveCamera(42, 1, 0.1, 80);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: !this.mobile, alpha: false, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.mobile ? 1.5 : 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    if (!this.mobile) {
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    this.hemi = new THREE.HemisphereLight(0xe2eeff, 0x4b3d2a, 1.15);
    this.sun = new THREE.DirectionalLight(0xfff1db, 2.4);
    this.sun.position.set(10, 16, 7);
    this.fill = new THREE.DirectionalLight(0x8aa6cc, 0.35);
    this.fill.position.set(-8, 6, -10);
    if (!this.mobile) {
      this.sun.castShadow = true;
      this.sun.shadow.mapSize.set(1024, 1024);
      this.sun.shadow.camera.near = 2;
      this.sun.shadow.camera.far = 40;
      this.sun.shadow.camera.left = -16;
      this.sun.shadow.camera.right = 16;
      this.sun.shadow.camera.top = 16;
      this.sun.shadow.camera.bottom = -16;
    }
    this.scene.add(this.hemi, this.sun, this.sun.target, this.fill);

    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(28, 48),
      new THREE.MeshLambertMaterial({ color: 0x1a2838 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.04;
    ground.receiveShadow = !this.mobile;
    this.ground = ground;
    this.scene.add(ground);

    this.geos = {
      hex: new THREE.CylinderGeometry(TILE_R, TILE_R, 1, 6),
      ring: new THREE.RingGeometry(HEX_SIZE * 0.72, HEX_SIZE * 0.96, 6),
      thinRing: new THREE.RingGeometry(HEX_SIZE * 0.8, HEX_SIZE * 0.9, 6),
      fill: new THREE.CircleGeometry(HEX_SIZE * 0.86, 6),
      inf: new THREE.CapsuleGeometry(0.13, 0.26, 3, 8),
      head: new THREE.SphereGeometry(0.11, 8, 6),
      box: new THREE.BoxGeometry(1, 1, 1),
      cyl: new THREE.CylinderGeometry(1, 1, 1, 8),
      bar: new THREE.PlaneGeometry(0.62, 0.1),
      barBg: new THREE.PlaneGeometry(0.7, 0.17),
    };
    // CylinderGeometry already puts a vertex on +z, which is pointy-top in this layout; no extra rotation.
    this.geos.ring.rotateX(-Math.PI / 2);
    this.geos.thinRing.rotateX(-Math.PI / 2);
    this.geos.fill.rotateX(-Math.PI / 2);
    // CircleGeometry/RingGeometry put a vertex at angle 0; rotate so the hexagons line up with pointy-top tiles.
    for (const g of [this.geos.ring, this.geos.thinRing, this.geos.fill]) g.rotateY(Math.PI / 6);

    this.tileMesh = null;
    this.markMesh = null;
    this.fillMesh = null;
    this.edgeLines = null;
    this.props = null;
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.screenSpacePanning = false; // right-drag / two-finger pan slides along the ground
    this.controls.addEventListener("change", () => this._clampTarget());
    this.controls.enableDamping = !this.reduceMotion;
    this.controls.dampingFactor = 0.08;
    this.controls.maxPolarAngle = 1.2;
    this.controls.minPolarAngle = 0.32;
    this.controls.minDistance = 7;
    this.controls.maxDistance = 26;
    this.controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    this.insets = { top: 0, bottom: 0 };
    this.dirty = true;
    this.resize();
  }

  // Shift the projection so the board centres in the strip between the top bar and the dock, not behind them.
  setInsets(top, bottom) {
    if (this.insets.top === top && this.insets.bottom === bottom) return;
    this.insets = { top, bottom };
    this._applyOffset();
  }

  _applyOffset() {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.camera.setViewOffset(w, h, 0, (this.insets.bottom - this.insets.top) / 2, w, h);
    this.camera.updateProjectionMatrix();
    this.dirty = true;
  }

  resize() {
    const w = this.canvas.parentElement?.clientWidth || window.innerWidth;
    const h = this.canvas.parentElement?.clientHeight || window.innerHeight;
    this.camera.aspect = w / Math.max(1, h);
    this.renderer.setSize(w, h, false);
    this._applyOffset();
    this.dirty = true;
  }

  get large() { return (this.bounds?.span ?? 0) > 30; }

  frameHome(state = this.state) {
    if (!state || !this.bounds) return;
    const { cx, cz, span } = this.bounds;
    const hb = state.bases.find((b) => b.owner === 1), fb = state.bases.find((b) => b.owner === 2);
    const home = axialToWorld(hb.q, hb.r), foe = axialToWorld(fb.q, fb.r);
    const dx = foe.x - home.x, dz = foe.z - home.z;
    const len = Math.hypot(dx, dz) || 1, ux = dx / len, uz = dz / len;
    // Portrait screens have a narrow horizontal FOV, so pull back until the view's width fits.
    const fit = Math.min(2.2, Math.max(1, 1.3 / this.camera.aspect));
    if (this.large) {
      // Big maps: look north from the south edge so the front runs left (home) to right (enemy), matching the
      // minimap. Frame the home front — including a starting fleet — then zoom out, pan or use the minimap.
      const navy = (state.units ?? []).filter((u) => u.owner === 1 && M.UNITS[u.type]?.naval);
      const port = (state.structures ?? []).find((s) => s.owner === 1 && (s.type === "dock" || s.type === "coastal"));
      const pts = [home, ...navy.map((u) => axialToWorld(u.q, u.r))];
      if (port) pts.push(axialToWorld(port.q, port.r));
      const sx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
      const sz = pts.reduce((a, p) => a + p.z, 0) / pts.length;
      const tx = sx + ux * 5, tz = sz + uz * 5;
      const back = (navy.length ? 18 : 12) * fit;
      this.controls.target.set(tx, 0.35, tz);
      this.camera.position.set(tx, (navy.length ? 20 : 16.5) * fit, tz + back);
      this.controls.maxDistance = Math.max(span * 1.15, 40);
    } else {
      // Aim a little toward home so the base, its drop hexes and the army stay in view above the dock.
      const tx = cx + (home.x - cx) * 0.75, tz = cz + (home.z - cz) * 0.75;
      const back = 10.5 * fit;
      this.controls.target.set(tx, 0.35, tz);
      this.camera.position.set(home.x - ux * back, 10.5 * fit, home.z - uz * back);
      this.controls.maxDistance = Math.max(22, 18 * fit);
    }
    this.controls.minDistance = 5;
    this.dirty = true;
  }

  /** Whole-board view from the south, sized so the board's width fits the screen. */
  frameOverview() {
    if (!this.bounds) return;
    const { cx, cz, minX, maxX, minZ, maxZ } = this.bounds;
    const fovV = (this.camera.fov * Math.PI) / 180;
    const fovH = 2 * Math.atan(Math.tan(fovV / 2) * this.camera.aspect);
    const w = maxX - minX + 3, d = maxZ - minZ + 3;
    const dist = Math.max((w / 2) / Math.tan(fovH / 2), (d * 0.9) / Math.tan(fovV / 2)) * 0.95;
    const tilt = 1.0; // ~57° below horizontal
    this.controls.maxDistance = Math.max(this.controls.maxDistance, dist * 1.05);
    this.controls.target.set(cx, 0.35, cz);
    this.camera.position.set(cx, Math.sin(tilt) * dist, cz + Math.cos(tilt) * dist);
    this.controls.update();
    this.dirty = true;
  }

  // Scene scale follows the board: fog, far plane, shadow frustum, backdrop.
  _fitScene() {
    const { cx, cz, span } = this.bounds;
    this.scene.fog.near = Math.max(32, span * 0.9);
    this.scene.fog.far = Math.max(58, span * 2.2);
    this.camera.far = Math.max(80, span * 3);
    this.camera.updateProjectionMatrix();
    this.ground.position.set(cx, -0.04, cz);
    this.ground.scale.setScalar(Math.max(1, (span * 0.95) / 28));
    const half = Math.max(16, span * 0.62);
    this.sun.position.set(cx + 10 + span * 0.15, 16 + span * 0.35, cz + 7 + span * 0.1);
    this.sun.target.position.set(cx, 0, cz);
    if (!this.mobile) {
      const sc = this.sun.shadow.camera;
      Object.assign(sc, { left: -half, right: half, top: half, bottom: -half, far: 40 + span * 2 });
      sc.updateProjectionMatrix();
      const size = span > 30 ? 2048 : 1024;
      if (this.sun.shadow.mapSize.x !== size) {
        this.sun.shadow.mapSize.set(size, size);
        this.sun.shadow.map?.dispose();
        this.sun.shadow.map = null;
      }
    }
  }

  _clampTarget() {
    if (!this.bounds) return;
    const t = this.controls.target, b = this.bounds;
    const x = Math.max(b.minX, Math.min(b.maxX, t.x)), z = Math.max(b.minZ, Math.min(b.maxZ, t.z));
    if (x !== t.x || z !== t.z) {
      this.camera.position.x += x - t.x;
      this.camera.position.z += z - t.z;
      t.x = x; t.z = z;
    }
  }

  /** Slide the camera and its target together along the ground. */
  panBy(dx, dz) {
    this.controls.target.x += dx; this.controls.target.z += dz;
    this.camera.position.x += dx; this.camera.position.z += dz;
    this._clampTarget();
    this.controls.update();
  }

  panTo(x, z) { this.panBy(x - this.controls.target.x, z - this.controls.target.z); }

  /** Pan relative to the view: +right / +forward in screen terms, scaled by zoom. */
  panView(right, forward) {
    const t = this.controls.target, c = this.camera.position;
    let fx = t.x - c.x, fz = t.z - c.z;
    const len = Math.hypot(fx, fz) || 1;
    fx /= len; fz /= len;
    const step = c.distanceTo(t) * 0.12;
    this.panBy((fx * forward - fz * right) * step, (fz * forward + fx * right) * step);
  }

  /** Ground footprint of the view (world x/z of the four screen corners), for the minimap. */
  footprint() {
    if (!this.bounds) return null;
    const b = this.bounds, out = [];
    const clampX = (v) => Math.max(b.minX - 1, Math.min(b.maxX + 1, v));
    const clampZ = (v) => Math.max(b.minZ - 1, Math.min(b.maxZ + 1, v));
    for (const [x, y] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      this._ndc.set(x, y);
      this._ray.setFromCamera(this._ndc, this.camera);
      const ray = this._ray.ray;
      // Corners above the horizon never meet the ground: push them far out along the view instead.
      if (!ray.intersectPlane(this._plane, this._hit)) this._hit.copy(ray.origin).addScaledVector(ray.direction, b.span * 3);
      out.push({ x: clampX(this._hit.x), z: clampZ(this._hit.z) });
    }
    return out;
  }

  /** Displayed top surface height of a hex (terrain height plus relief). */
  _top(h, st = this.state) {
    const k = M.key(h);
    const base = st.bases.some((b) => b.q === h.q && b.r === h.r);
    return tileHeight(base ? "base" : st.tiles[k] || "plains") + (st.relief?.[k] ?? 0) * RELIEF;
  }

  attachBoard(state) {
    if (this.tileMesh) {
      this.scene.remove(this.tileMesh);
      this.tileMesh.geometry.dispose();
      this.tileMesh.material.dispose();
    }
    this.state = state;
    this.cells = M.boardCells(state);
    const count = this.cells.length;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const h of this.cells) {
      const w = axialToWorld(h.q, h.r);
      minX = Math.min(minX, w.x); maxX = Math.max(maxX, w.x); minZ = Math.min(minZ, w.z); maxZ = Math.max(maxZ, w.z);
    }
    this.bounds = { minX, maxX, minZ, maxZ, cx: (minX + maxX) / 2, cz: (minZ + maxZ) / 2, span: Math.hypot(maxX - minX, maxZ - minZ) };
    this._fitScene();
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.93, metalness: 0, map: detailTexture() });
    const mesh = new THREE.InstancedMesh(this.geos.hex, mat, count);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.receiveShadow = !this.mobile;
    mesh.castShadow = !this.mobile;
    this.tileIndex.clear();
    this.oreIndex = [];
    this.waterIndex = [];
    this.cells.forEach(({ q, r }, i) => {
      this.tileIndex.set(M.key({ q, r }), i);
      const id = state.tiles[M.key({ q, r })] || "plains";
      if (id === "ore") this.oreIndex.push({ i, q, r });
      else if (id === "water") this.waterIndex.push({ i, q, r });
    });
    this.tileMesh = mesh;
    this.scene.add(mesh);

    this._buildEdges(state);
    this._buildProps(state);
    this._buildSea(state);
    for (const rec of this.structures.values()) this.scene.remove(rec.group);
    this.structures.clear();

    if (this.fillMesh) this.scene.remove(this.fillMesh);
    const fillMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.42, side: THREE.DoubleSide, depthWrite: false });
    this._fillMat = fillMat;
    this.fillMesh = new THREE.InstancedMesh(this.geos.fill, fillMat, 320);
    this.fillMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.fillMesh.frustumCulled = false;
    this.scene.add(this.fillMesh);

    if (this.markMesh) this.scene.remove(this.markMesh);
    const markMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false });
    this.markMesh = new THREE.InstancedMesh(this.geos.ring, markMat, 280);
    this.markMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.markMesh.frustumCulled = false;
    this.scene.add(this.markMesh);

    for (const rec of this.units.values()) rec.placed = false;
    this.frameHome(state);
    this._syncTileLayout();
    this._syncTileColors(0, false);
    this.dirty = true;
  }

  // Dark top-edge outline on every tile so neighbouring hexes read as separate cells. Terrain is static, so build once.
  _buildEdges(state) {
    if (this.edgeLines) {
      this.scene.remove(this.edgeLines);
      this.edgeLines.geometry.dispose();
    }
    const pts = [];
    const R = TILE_R;
    for (const { q, r } of this.cells) {
      const y = this._top({ q, r }, state) + 0.006;
      const w = axialToWorld(q, r);
      for (let k = 0; k < 6; k++) {
        const a0 = Math.PI / 6 + (k * Math.PI) / 3, a1 = a0 + Math.PI / 3;
        pts.push(w.x + R * Math.cos(a0), y, w.z + R * Math.sin(a0), w.x + R * Math.cos(a1), y, w.z + R * Math.sin(a1));
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    this.edgeLines = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x1b2416, transparent: true, opacity: 0.22 }));
    this.scene.add(this.edgeLines);
  }

  // Static scenery: mountain peaks, ore crystals, spike pits, stairs, ladders, jump pads. Built once per board.
  _buildProps(state) {
    if (this.props) {
      this.scene.remove(this.props);
      this.props.traverse((o) => { if (o.isLine || o.isInstancedMesh) o.geometry.dispose?.(); if (o.isInstancedMesh) o.dispose(); });
    }
    const g = new THREE.Group();
    const at = (h) => axialToWorld(h.q, h.r);
    const top = (h) => this._top(h, state);
    const pits = [], trees = [], houses = [];
    for (const [k, id] of Object.entries(state.tiles)) {
      const [q, r] = k.split(",").map(Number);
      const c = axialToWorld(q, r), seed = q * 1.7 + r * 0.9, y = top({ q, r });
      if (id === "mountain") g.add(Models.buildPeak(c, y, seed));
      else if (id === "ore") g.add(Models.buildOre(c, y, seed));
      else if (id === "pit") pits.push(c);
      else if (id === "forest") trees.push({ x: c.x, y, z: c.z, seed: hash2(q, r) });
      else if (id === "ruins") houses.push({ x: c.x, y, z: c.z, seed: hash2(q, r) });
    }
    if (pits.length) g.add(Models.buildSpikes(pits, tileHeight("pit")));
    if (trees.length) {
      const forest = Models.buildForest(trees);
      forest.name = "lod-forest";
      g.add(forest);
    }
    if (houses.length) {
      const town = Models.buildTown(houses);
      town.name = "lod-town";
      g.add(town);
    }
    for (const l of state.links ?? []) {
      if (l.kind === "jump") {
        g.add(Models.buildJumpPad(at(l.a), at(l.b), top(l.a)), Models.buildJumpPad(at(l.b), at(l.a), top(l.b)));
        g.add(Models.buildJumpArc(at(l.a), at(l.b), Math.max(top(l.a), top(l.b))));
        continue;
      }
      const [ground, high] = M.levelOf(state, l.a) < M.levelOf(state, l.b) ? [l.a, l.b] : [l.b, l.a];
      const build = l.kind === "ladder" ? Models.buildLadder : Models.buildStairs;
      g.add(build(at(ground), at(high), top(ground), top(high)));
    }
    g.traverse((o) => { if (o.isMesh) { o.castShadow = !this.mobile; o.receiveShadow = !this.mobile; } });
    this.props = g;
    this.scene.add(g);
  }

  // One translucent sea surface at sea level across the board and out to the horizon.
  _buildSea(state) {
    if (this.sea) { this.scene.remove(this.sea); this.sea.geometry.dispose(); }
    const hasWater = Object.values(state.tiles).some((id) => id === "water" || id === "ford");
    const { cx, cz, span } = this.bounds;
    const geo = new THREE.PlaneGeometry(span * 6, span * 6);
    geo.rotateX(-Math.PI / 2);
    this.sea = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
      color: 0x2c6787, transparent: true, opacity: hasWater ? 0.78 : 0.95, roughness: 0.18, metalness: 0.05,
    }));
    this.sea.position.set(cx, hasWater ? 0.11 : 0.02, cz);
    this.sea.receiveShadow = !this.mobile;
    this.scene.add(this.sea);
    this.ground.visible = false;
  }

  setView(state, view) {
    this.state = state;
    this.view = view;
    this._syncTileLayout();
    this._syncTileColors(performance.now(), false);
    this._syncMarks();
    this._syncUnits();
    this._syncBases();
    this._syncStructures();
    this._syncLock();
    this.dirty = true;
  }

  /** Screen position (CSS px) of a hex, for DOM juice like damage numbers. View-only. */
  screenOf(h, lift = 0.9) {
    const w = axialToWorld(h.q, h.r);
    const v = new THREE.Vector3(w.x, lift, w.z).project(this.camera);
    const rect = this.renderer?.domElement?.getBoundingClientRect?.() ?? { left: 0, top: 0, width: innerWidth, height: innerHeight };
    return { x: rect.left + (v.x + 1) / 2 * rect.width, y: rect.top + (1 - v.y) / 2 * rect.height };
  }

  pick(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this._ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this._ray.setFromCamera(this._ndc, this.camera);
    if (!this._ray.ray.intersectPlane(this._plane, this._hit)) return null;
    const h = worldToAxial(this._hit.x, this._hit.z);
    if (!this.state || !M.inBounds(this.state, h)) return null;
    return h;
  }

  _tileColor(q, r, time, still = false) {
    const k = M.key({ q, r });
    const terrain = this.state.tiles[k] || "plains";
    const base = this.state.bases.find((b) => b.q === q && b.r === r);
    let hex = TERRAIN[terrain] || TERRAIN.plains;
    if (base) hex = base.owner === 1 ? 0x1c3a5c : 0x5c1e1c;
    else {
      // Sandy shore where open ground meets water.
      if ((terrain === "plains" || terrain === "road") && M.DIRS.some((d) => this.state.tiles[M.key(M.add({ q, r }, d))] === "water")) {
        hex = mixHex(hex, SAND, 0.6);
      }
      // Subtle per-hex jitter and relief shading so open ground reads as land, not a checkerboard.
      hex = shade(hex, 0.94 + hash2(q, r) * 0.1 + (this.state.relief?.[k] ?? 0) * 0.035);
    }
    const inf = M.influenceAt(this.state, { q, r });
    if (inf && !base && terrain !== "mountain") {
      const owned = !!this.state.oreOwner?.[k];
      hex = mixHex(hex, inf === 1 ? GVS : BOE, owned ? 0.42 : 0.16);
    }
    if (terrain === "ore" && !this.reduceMotion && !still) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 0.002 + q * 0.7 + r);
      hex = mixHex(hex, 0xf0c040, 0.15 + pulse * 0.18);
    }
    if (terrain === "water" && !this.reduceMotion && !still) {
      const ripple = 0.5 + 0.5 * Math.sin(time * 0.0012 + q * 0.9 - r * 0.6);
      hex = mixHex(hex, 0x5aa8d0, ripple * 0.18);
    }
    return hex;
  }

  _syncTileLayout() {
    if (!this.tileMesh || !this.state) return;
    const dummy = this._dummy;
    this.cells.forEach(({ q, r }, i) => {
      const h = this._top({ q, r });
      const w = axialToWorld(q, r);
      dummy.position.set(w.x, h / 2, w.z);
      dummy.scale.set(1, h, 1);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      this.tileMesh.setMatrixAt(i, dummy.matrix);
    });
    this.tileMesh.instanceMatrix.needsUpdate = true;
  }

  /** Static terrain colour (no animation), for the minimap. */
  tileColor(q, r) { return this._tileColor(q, r, 0, true); }

  _syncTileColors(time, oreOnly) {
    if (!this.tileMesh || !this.state) return;
    if (oreOnly) {
      for (const { i, q, r } of this.oreIndex) this.tileMesh.setColorAt(i, this._scratchCol.setHex(this._tileColor(q, r, time)));
      for (const { i, q, r } of this.waterIndex) this.tileMesh.setColorAt(i, this._scratchCol.setHex(this._tileColor(q, r, time)));
    } else {
      this.cells.forEach(({ q, r }, i) => this.tileMesh.setColorAt(i, this._scratchCol.setHex(this._tileColor(q, r, time))));
    }
    if (this.tileMesh.instanceColor) this.tileMesh.instanceColor.needsUpdate = true;
  }

  _placeMark(mesh, i, q, r, y, color, s = 1) {
    const w = axialToWorld(q, r);
    const dummy = this._dummy;
    dummy.position.set(w.x, y, w.z);
    dummy.scale.set(s, 1, s);
    dummy.rotation.set(0, 0, 0);
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
    mesh.setColorAt(i, this._scratchCol.setHex(color));
  }

  // Overlays sit just above the tile they mark, so a mark on a ruin or ore vein is not buried in the tile.
  _lift(h, extra) {
    return this._top(h) + extra;
  }

  _syncMarks() {
    if (!this.markMesh || !this.state) return;
    for (const mesh of [this.markMesh, this.fillMesh]) {
      for (let i = 0; i < mesh.count; i++) mesh.setMatrixAt(i, this._hide.matrix);
    }
    let i = 0, f = 0;
    // Filled hexes answer "where can I go / place"; rings answer "who can hit what".
    const fill = (h, col, lift) => {
      if (f >= this.fillMesh.count || h == null) return;
      this._placeMark(this.fillMesh, f++, h.q, h.r, this._lift(h, lift), col);
    };
    const ring = (h, col, lift, s = 1) => {
      if (i >= this.markMesh.count || h == null) return;
      this._placeMark(this.markMesh, i++, h.q, h.r, this._lift(h, lift), col, s);
    };
    for (const t of this.view.reach || []) fill(t, MARK.reach, 0.012);
    for (const t of this.view.drops || []) fill(t, MARK.drop, 0.014);
    for (const p of this.view.path || []) fill(p, MARK.path, 0.016);
    const seen = new Set();
    for (const t of this.view.threats || []) {
      const k = M.key(t);
      if (seen.has(k)) continue;
      seen.add(k);
      ring(t, MARK.threat, 0.02);
    }
    for (const t of this.view.attacks || []) ring(t, MARK.attack, 0.03, 1.08);
    if (this.view.hover && M.inBounds(this.state, this.view.hover)) ring(this.view.hover, MARK.hover, 0.035, 0.94);
    for (const id of this.view.selected || []) {
      const u = M.byId(this.state, id);
      if (u) ring(u, MARK.selected, 0.04, 0.8);
    }
    for (const mesh of [this.markMesh, this.fillMesh]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  _unitMat(owner, selected) {
    const key = `${owner}-${selected ? 1 : 0}`;
    this._umats ??= new Map();
    if (!this._umats.has(key)) {
      this._umats.set(key, new THREE.MeshLambertMaterial({
        color: owner === 1 ? GVS : BOE,
        emissive: selected ? 0x223344 : 0x000000,
        emissiveIntensity: selected ? 0.35 : 0,
      }));
    }
    return this._umats.get(key);
  }

  _darkMat(owner) {
    this._darks ??= new Map();
    if (!this._darks.has(owner)) {
      this._darks.set(owner, new THREE.MeshLambertMaterial({ color: owner === 1 ? 0x16324a : 0x4a1c16 }));
    }
    return this._darks.get(owner);
  }

  _discMat(owner) {
    this._discs ??= new Map();
    if (!this._discs.has(owner)) {
      this._discs.set(owner, new THREE.MeshBasicMaterial({
        color: owner === 1 ? GVS : BOE, transparent: true, opacity: 0.55, side: THREE.DoubleSide,
      }));
    }
    return this._discs.get(owner);
  }

  _buildUnit(type, owner) {
    const g = new THREE.Group();
    g.scale.setScalar(M.UNITS[type]?.naval ? 2.15 : 1.85);
    const alt = ALTITUDE[type] ?? 0;
    const disc = new THREE.Mesh(this.geos.ring, this._discMat(owner));
    disc.position.y = 0.05 - alt;
    disc.scale.setScalar(0.45);
    const body = Models.buildUnit(type, owner, this._unitMat(owner, false));
    body.name = "body";
    g.add(disc, body);
    if (M.UNITS[type]?.naval) {
      const wake = new THREE.Group();
      wake.name = "wake";
      const foam = new THREE.MeshBasicMaterial({ color: 0xd8eef6, transparent: true, opacity: 0.35, depthWrite: false });
      for (let i = 0; i < 3; i++) {
        const p = new THREE.Mesh(this.geos.fill, foam);
        p.position.set(-0.22 - i * 0.16, 0.02, 0);
        p.scale.setScalar(0.2 + i * 0.1);
        wake.add(p);
      }
      g.add(wake);
    }
    if (alt) {
      // Soft ground shadow and a faint tether so you can tell which hex an aircraft holds.
      const shadow = new THREE.Mesh(this.geos.fill, this._shadowMat());
      shadow.position.y = 0.03 - alt;
      shadow.scale.setScalar(0.42);
      const tether = new THREE.Mesh(this.geos.cyl, this._discMat(owner));
      tether.scale.set(0.006, alt - 0.1, 0.006);
      tether.position.y = -alt / 2;
      g.add(shadow, tether);
    }
    // Clash-style health bar: team-coloured fill on a dark plate, always facing the camera.
    const bar = new THREE.Mesh(this.geos.bar, new THREE.MeshBasicMaterial({ color: owner === 1 ? 0x3fa9ff : 0xff4b4b, depthTest: false }));
    bar.position.y = 0.82;
    bar.name = "hp";
    bar.renderOrder = 11;
    this._barBgMat ??= new THREE.MeshBasicMaterial({ color: 0x120a24, depthTest: false });
    const bg = new THREE.Mesh(this.geos.barBg, this._barBgMat);
    bg.name = "hpbg";
    bg.renderOrder = 10;
    bg.position.y = 0.82;
    g.add(bg, bar);
    g.traverse((o) => { if (o.isMesh) { o.castShadow = !this.mobile; o.receiveShadow = !this.mobile; } });
    return g;
  }

  _shadowMat() {
    this._shadow ??= new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.28, depthWrite: false });
    return this._shadow;
  }

  _disposeUnit(rec) {
    this.scene.remove(rec.group);
    rec.group.traverse((o) => {
      if (o.isMesh && o.name === "hp" && o.material) o.material.dispose();
    });
  }

  _syncUnits() {
    const seen = new Set();
    for (const u of this.state.units) {
      seen.add(u.id);
      let rec = this.units.get(u.id);
      if (!rec || rec.type !== u.type || rec.owner !== u.owner) {
        if (rec) this._disposeUnit(rec);
        const group = this._buildUnit(u.type, u.owner);
        const rotors = [];
        group.traverse((o) => { if (o.name === "rotor") rotors.push(o); });
        rec = {
          group,
          body: group.getObjectByName("body"),
          rotors,
          alt: ALTITUDE[u.type] ?? 0,
          type: u.type,
          owner: u.owner,
          placed: false,
          target: new THREE.Vector3(),
          targetYaw: 0,
          flash: 0,
          hp: u.hp,
        };
        this.scene.add(rec.group);
        this.units.set(u.id, rec);
      }
      const w = axialToWorld(u.q, u.r);
      // A move across a jump link arcs through the air instead of sliding over the spike pit.
      if (rec.placed && rec.hex && !this.reduceMotion && M.linkBetween(this.state, rec.hex, u)?.kind === "jump") {
        rec.hop = { from: rec.group.position.clone(), t: 0 };
      }
      rec.hex = { q: u.q, r: u.r };
      rec.target.set(w.x, this._top(u) + rec.alt + (M.UNITS[u.type]?.naval ? 0.14 : 0), w.z);
      rec.targetYaw = yawFor(u.facing);
      if (!rec.placed || this.reduceMotion) {
        rec.group.position.copy(rec.target);
        rec.group.rotation.y = rec.targetYaw;
        rec.placed = true;
      }
      rec.group.traverse((o) => {
        if (o.userData.paint) o.material = this._unitMat(u.owner, this.view.selected?.has(u.id));
      });
      const bar = rec.group.getObjectByName("hp");
      const max = M.UNITS[u.type].hp;
      const hp = u.hp ?? max;
      if (rec.hp != null && hp < rec.hp) rec.flash = 1;
      rec.hp = hp;
      if (bar) {
        const ratio = Math.max(0.05, hp / max);
        bar.scale.set(ratio, 1, 1);
        bar.position.x = 0;
        rec.barColor = ratio < 0.35 ? 0xffc531 : u.owner === 1 ? 0x3fa9ff : 0xff4b4b;
        bar.material.color.setHex(rec.barColor);
        bar.visible = true;
      }
      rec.group.scale.setScalar(u.fresh ? 0.92 : 1);
    }
    const now = performance.now();
    for (const [id, rec] of this.units) {
      if (seen.has(id)) continue;
      const until = this.wrecks.get(id) ?? 0;
      if (until > now) {
        rec.wreck = true;
        const bar = rec.group.getObjectByName("hp");
        if (bar) bar.visible = false;
        const bg = rec.group.getObjectByName("hpbg");
        if (bg) bg.visible = false;
        continue;
      }
      this.wrecks.delete(id);
      this._disposeUnit(rec);
      this.units.delete(id);
    }
  }

  holdWreck(id, ms) {
    this.wrecks.set(id, performance.now() + ms);
  }

  _syncBases() {
    for (const b of this.state.bases) {
      let rec = this.bases.get(b.owner);
      if (!rec) {
        const g = Models.buildBase(b.owner);
        g.traverse((o) => { if (o.isMesh) { o.castShadow = !this.mobile; } });
        this.scene.add(g);
        rec = { group: g };
        this.bases.set(b.owner, rec);
      }
      const w = axialToWorld(b.q, b.r);
      rec.group.position.set(w.x, this._top(b), w.z);
      rec.group.visible = (b.hp ?? 0) > 0;
      rec.threatened = (this.view.threats || []).some((t) => t.kind === "base" && t.targetBase === b.owner);
    }
  }

  _tickUnits(time) {
    // Breathing highlight so legal move / drop hexes read at a glance.
    if (this._fillMat) this._fillMat.opacity = this.reduceMotion ? 0.5 : 0.46 + 0.16 * Math.sin(time * 0.006);
    const snap = this.reduceMotion ? 1 : 0.18;
    for (const rec of this.units.values()) {
      if (rec.hop) {
        rec.hop.t = Math.min(1, rec.hop.t + 0.035);
        const t = rec.hop.t;
        rec.group.position.lerpVectors(rec.hop.from, rec.target, t);
        rec.group.position.y += Math.sin(Math.PI * t) * 1.5;
        if (t >= 1) rec.hop = null;
      } else {
        const moving = !rec.alt && !this.reduceMotion && rec.group.position.distanceTo(rec.target) > 0.05;
        rec.group.position.lerp(rec.target, snap);
        // Little march hop while a ground unit slides to its new hex.
        if (moving) rec.group.position.y = rec.target.y + Math.abs(Math.sin(time * 0.03)) * 0.12;
      }
      rec.group.rotation.y = this.reduceMotion
        ? rec.targetYaw
        : rec.group.rotation.y + (shortestYaw(rec.group.rotation.y, rec.targetYaw) - rec.group.rotation.y) * 0.2;
      if (rec.rotors.length && !this.reduceMotion) {
        for (const r of rec.rotors) r.rotation[r.userData.axis === "z" ? "z" : "y"] += 0.55;
      }
      if (rec.alt && rec.body && !this.reduceMotion) rec.body.position.y = Math.sin(time * 0.003 + rec.alt * 7) * 0.05;
      const wake = rec.group.getObjectByName("wake");
      if (wake && !this.reduceMotion) {
        const moving = rec.group.position.distanceTo(rec.target) > 0.04;
        wake.visible = true;
        wake.children.forEach((p, i) => { p.material.opacity = moving ? 0.38 - i * 0.08 : 0.18 - i * 0.04; });
      }
      const bar = rec.group.getObjectByName("hp");
      if (bar) {
        bar.lookAt(this.camera.position);
        rec.group.getObjectByName("hpbg")?.lookAt(this.camera.position);
        if (rec.flash > 0.02) {
          rec.flash *= this.reduceMotion ? 0 : 0.86;
          bar.material.color.setHex(0xffffff);
          if (rec.flash <= 0.02 && rec.barColor != null) bar.material.color.setHex(rec.barColor);
        }
      }
    }
    for (const rec of this.bases.values()) {
      const s = rec.threatened && !this.reduceMotion ? 1 + 0.035 * Math.sin(time * 0.01) : 1;
      rec.group.scale.setScalar(s);
    }
  }

  // ---------------------------------------------------------------- structures
  _syncStructures() {
    const seen = new Set();
    for (const s of this.state.structures ?? []) {
      seen.add(s.id);
      let rec = this.structures.get(s.id);
      if (!rec) {
        const g = Models.buildStructure(s.type, s.owner);
        g.traverse((o) => { if (o.isMesh) { o.castShadow = !this.mobile; o.receiveShadow = !this.mobile; } });
        const bar = new THREE.Mesh(this.geos.bar, new THREE.MeshBasicMaterial({ color: 0x6fbf7a }));
        bar.position.y = 0.95;
        bar.name = "hp";
        g.add(bar);
        g.scale.setScalar(1.25);
        this.scene.add(g);
        rec = { group: g, head: g.getObjectByName("head"), bar, yaw: 0, flash: 0, hp: s.hp };
        this.structures.set(s.id, rec);
      }
      const w = axialToWorld(s.q, s.r);
      rec.group.position.set(w.x, this._top(s), w.z);
      // Track the nearest enemy it could engage, else face the enemy base.
      const t = M.STRUCTURES[s.type];
      let aim = null, best = Infinity;
      for (const u of this.state.units) {
        if (u.owner === s.owner) continue;
        const d = M.dist(s, u);
        if (d <= t.range[1] + 2 && d < best && M.vsAgainst(t, u) > 0) { best = d; aim = u; }
      }
      aim ??= this.state.bases.find((b) => b.owner !== s.owner);
      if (aim) { const a = axialToWorld(aim.q, aim.r); rec.yaw = Math.atan2(-(a.z - w.z), a.x - w.x); }
      if (s.hp < rec.hp) rec.flash = 1;
      rec.hp = s.hp;
      rec.bar.scale.set(Math.max(0.05, s.hp / M.structureHp(this.state, s.type)), 1, 1);
    }
    for (const [id, rec] of this.structures) {
      if (!seen.has(id)) { this.scene.remove(rec.group); rec.bar.material.dispose(); this.structures.delete(id); }
    }
  }

  // ---------------------------------------------------------------- lock-on reticle
  _syncLock() {
    const lock = this.view.lock;
    if (!this.lockMesh) {
      const g = new THREE.Group();
      const red = new THREE.MeshBasicMaterial({ color: 0xff3b30, transparent: true, opacity: 0.95, depthTest: false });
      for (let k = 0; k < 4; k++) {
        const corner = new THREE.Group();
        corner.rotation.y = (k * Math.PI) / 2;
        const a = new THREE.Mesh(this.geos.box, red); a.scale.set(0.26, 0.03, 0.05); a.position.set(0.5, 0, 0.63);
        const b = new THREE.Mesh(this.geos.box, red); b.scale.set(0.05, 0.03, 0.26); b.position.set(0.63, 0, 0.5);
        corner.add(a, b);
        g.add(corner);
      }
      const ring = new THREE.Mesh(this.geos.thinRing, red);
      ring.scale.setScalar(0.62);
      g.add(ring);
      const dot = new THREE.Mesh(this.geos.fill, red);
      dot.scale.setScalar(0.06);
      g.add(dot);
      g.renderOrder = 10;
      g.traverse((o) => { o.renderOrder = 10; });
      this.lockMesh = g;
      this.scene.add(g);
    }
    this.lockMesh.visible = !!lock;
    if (!lock) return;
    const w = axialToWorld(lock.q, lock.r);
    this.lockMesh.position.set(w.x, this._top(lock) + (lock.air ? ALTITUDE[lock.type] ?? 1.2 : 0.06), w.z);
    this.lockMesh.userData.hard = !!lock.hard;
  }

  // ---------------------------------------------------------------- combat effects
  /** Queue shots: [{ from:{q,r}, to:{q,r}, weapon, fromAir, toAir, destroyed }]. Staggered so a volley reads in order. */
  fire(shots) {
    if (!shots?.length) return;
    const now = performance.now();
    shots.forEach((s, i) => this._shot(s, now + i * 260));
  }

  _point(h, air, type) {
    const w = axialToWorld(h.q, h.r);
    return new THREE.Vector3(w.x, this._top(h) + (air ? ALTITUDE[type] ?? 1.2 : 0.35), w.z);
  }

  _fxMat(color) {
    this._fxMats ??= new Map();
    if (!this._fxMats.has(color)) this._fxMats.set(color, new THREE.MeshBasicMaterial({ color, toneMapped: false }));
    return this._fxMats.get(color);
  }

  _shot(s, start) {
    const a = this._point(s.from, s.fromAir, s.weapon), b = this._point(s.to, s.toAir, s.toType);
    const kind = s.weapon === "artillery" || s.weapon === "battleship" ? "shell"
      : s.weapon === "jet" || s.weapon === "helicopter" || s.weapon === "aa" ? (s.weapon === "aa" ? "flak" : "missile")
      : "tracer";
    const slow = this.fxScale ?? 1; // >1 only for slow-motion captures
    const dur = this.reduceMotion ? 1 : (kind === "shell" ? 900 : kind === "missile" ? 650 : 300) * slow;
    const dist = a.distanceTo(b);
    if (!this.reduceMotion) {
      const flash = new THREE.Mesh(this.geos.head, new THREE.MeshBasicMaterial({ color: 0xfff4c2, transparent: true, toneMapped: false, depthWrite: false }));
      flash.position.copy(a);
      this.scene.add(flash);
      this.fx.push({ mesh: flash, start, dur: 90 * slow, puff: true, update: (t) => { flash.scale.setScalar(0.25 + t * 0.6); flash.material.opacity = 1 - t; } });
    }
    if (s.destroyed && s.targetId) this.holdWreck(s.targetId, (dur + 720 * slow));
    if (kind === "tracer" || kind === "flak") {
      for (let i = 0; i < 3; i++) {
        const m = new THREE.Mesh(this.geos.box, this._fxMat(kind === "flak" ? 0xffd27a : 0xfff1a8));
        m.scale.set(Math.min(0.5, dist * 0.18), 0.025, 0.025);
        m.visible = false;
        this.scene.add(m);
        this.fx.push({ mesh: m, start: start + i * 70, dur, update: (t) => { m.position.lerpVectors(a, b, t); m.lookAt(b); m.rotateY(Math.PI / 2); } });
      }
    } else {
      const m = new THREE.Mesh(kind === "shell" ? this.geos.head : this.geos.cyl, this._fxMat(kind === "shell" ? 0x2b2b2b : 0xe8e8e8));
      if (kind === "shell") m.scale.setScalar(0.6); else m.scale.set(0.03, 0.22, 0.03);
      m.visible = false;
      this.scene.add(m);
      const arc = kind === "shell" ? Math.max(2, dist * 0.45) : dist * 0.12;
      const prev = new THREE.Vector3();
      this.fx.push({ mesh: m, start, dur, trail: kind === "missile", update: (t) => {
        prev.copy(m.position);
        m.position.lerpVectors(a, b, t);
        m.position.y += Math.sin(Math.PI * t) * arc;
        if (kind === "missile" && t > 0) { m.lookAt(prev); m.rotateX(Math.PI / 2); }
      } });
    }
    this.fx.push({ start: start + dur, dur: this.reduceMotion ? 1 : 720 * slow, boom: { at: b, big: !!s.destroyed } });
  }

  _tickFx(now) {
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      if (now < f.start) continue;
      const t = Math.min(1, (now - f.start) / f.dur);
      if (f.boom) {
        if (!f.mesh) {
          const m = new THREE.Mesh(this.geos.head, new THREE.MeshBasicMaterial({ color: 0xffa040, transparent: true, toneMapped: false, depthWrite: false }));
          m.position.copy(f.boom.at);
          this.scene.add(m);
          f.mesh = m;
          const k = f.boom.big ? 1 : 0.55;
          for (let n = 0; n < (f.boom.big ? 5 : 3); n++) {
            const puff = new THREE.Mesh(this.geos.head, new THREE.MeshBasicMaterial({ color: 0xc5c8cc, transparent: true, opacity: 0.45, depthWrite: false }));
            puff.position.copy(f.boom.at);
            this.scene.add(puff);
            const dir = new THREE.Vector3((Math.random() - 0.5) * 1.2, 0.4 + Math.random() * 0.8, (Math.random() - 0.5) * 1.2);
            this.fx.push({ mesh: puff, start: now, dur: 700, puff: true, update: (u) => {
              puff.position.copy(f.boom.at).addScaledVector(dir, u);
              puff.scale.setScalar((0.5 + u * 2.2) * k);
              puff.material.opacity = 0.45 * (1 - u);
            } });
          }
          for (let n = 0; n < (f.boom.big ? 8 : 4); n++) {
            const shard = new THREE.Mesh(this.geos.box, this._fxMat(0x6a5040));
            shard.position.copy(f.boom.at);
            shard.scale.set(0.06, 0.06, 0.12);
            this.scene.add(shard);
            const dir = new THREE.Vector3((Math.random() - 0.5) * 3, 1.2 + Math.random() * 2, (Math.random() - 0.5) * 3);
            this.fx.push({ mesh: shard, start: now, dur: 520, puff: true, update: (u) => {
              shard.position.copy(f.boom.at).addScaledVector(dir, u);
              shard.rotation.x += 0.2; shard.rotation.z += 0.15;
              shard.material.opacity = 1;
            } });
          }
        }
        const k = f.boom.big ? 3.4 : 1.8;
        f.mesh.scale.setScalar((0.7 + t * 5.2) * k);
        f.mesh.material.opacity = 1 - t;
        f.mesh.material.color.setHex(t < 0.25 ? 0xfff4c4 : t < 0.55 ? 0xff7a2a : 0x4a3228);
      } else {
        f.mesh.visible = true;
        f.update(t);
        if (f.trail && Math.random() < 0.6) {
          const puff = new THREE.Mesh(this.geos.head, new THREE.MeshBasicMaterial({ color: 0xcfd4da, transparent: true, opacity: 0.5, depthWrite: false }));
          puff.position.copy(f.mesh.position);
          this.scene.add(puff);
          this.fx.push({ mesh: puff, start: now, dur: 600, puff: true, update: (u) => { puff.scale.setScalar(0.4 + u * 1.6); puff.material.opacity = 0.5 * (1 - u); } });
        }
      }
      if (t >= 1) {
        if (f.mesh) {
          this.scene.remove(f.mesh);
          if (f.boom || f.puff) f.mesh.material.dispose();
        }
        this.fx.splice(i, 1);
      }
    }
  }

  get busyFx() { return this.fx.length > 0; }

  _tickLod() {
    if (!this.props || !this.bounds) return;
    const dist = this.camera.position.distanceTo(this.controls.target);
    const far = dist > Math.max(20, this.bounds.span * 0.26);
    const forest = this.props.getObjectByName("lod-forest");
    const town = this.props.getObjectByName("lod-town");
    if (forest) forest.visible = !far;
    if (town) town.visible = !far;
  }

  render(time) {
    this.controls.update();
    this._tickLod();
    if (this.tileMesh && this.state) this._syncTileColors(time, true);
    this._tickUnits(time);
    this._tickFx(performance.now());
    for (const rec of this.structures.values()) {
      if (rec.head) rec.head.rotation.y += (shortestYaw(rec.head.rotation.y, rec.yaw) - rec.head.rotation.y) * (this.reduceMotion ? 1 : 0.12);
      rec.bar.lookAt(this.camera.position);
      if (rec.flash > 0.02) { rec.flash *= 0.86; rec.bar.material.color.setHex(0xfff4c2); } else rec.bar.material.color.setHex(0x6fbf7a);
    }
    if (this.lockMesh?.visible) {
      const hard = this.lockMesh.userData.hard;
      this.lockMesh.rotation.y = this.reduceMotion ? 0 : time * (hard ? 0.004 : 0.0015);
      const pulse = hard ? 0.92 + 0.08 * Math.sin(time * 0.02) : 1.1 + 0.08 * Math.sin(time * 0.006);
      this.lockMesh.scale.setScalar(this.reduceMotion ? 1 : pulse);
    }
    this.renderer.render(this.scene, this.camera);
  }
}
